import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { User } from '@/types';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  isAdmin: boolean;
  emailVerified: boolean;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user is admin
  const checkAdminStatus = async (user: FirebaseUser): Promise<boolean> => {
    try {
      // Check custom claims first
      const tokenResult = await user.getIdTokenResult();
      if (tokenResult.claims.admin === true) {
        return true;
      }

      // Check adminEmails collection
      if (user.email) {
        const adminEmailDoc = await getDoc(doc(db, 'adminEmails', user.email));
        return adminEmailDoc.exists();
      }

      return false;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  };

  // Sync user profile to Firestore
  const syncUserProfile = async (user: FirebaseUser, provider: 'email' | 'google' | 'twitter' = 'email') => {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    const adminStatus = await checkAdminStatus(user);
    setIsAdmin(adminStatus);

    const userData: Partial<User> = {
      email: user.email!,
      displayName: user.displayName,
      photoURL: user.photoURL,
      authProvider: provider,
      role: adminStatus ? 'ADMIN' : 'USER',
      updatedAt: serverTimestamp() as any,
    };

    if (!userDoc.exists()) {
      // Create new user document
      await setDoc(userRef, {
        ...userData,
        createdAt: serverTimestamp(),
      });
    } else {
      // Update existing user document
      await setDoc(userRef, userData, { merge: true });
    }

    // Fetch updated profile
    const updatedDoc = await getDoc(userRef);
    setUserProfile({ id: user.uid, ...updatedDoc.data() } as User);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Google sign-in is always verified, email/password needs verification
        setEmailVerified(user.emailVerified || user.providerData[0]?.providerId === 'google.com');
        await syncUserProfile(user);
      } else {
        setUserProfile(null);
        setIsAdmin(false);
        setEmailVerified(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    // Send verification email
    await sendEmailVerification(user);
    // Update profile with display name
    await setDoc(doc(db, 'users', user.uid), {
      email,
      displayName,
      photoURL: null,
      authProvider: 'email',
      role: 'USER',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const resendVerificationEmail = async () => {
    if (currentUser && !currentUser.emailVerified) {
      await sendEmailVerification(currentUser);
    }
  };

  const refreshUser = async () => {
    if (currentUser) {
      await currentUser.reload();
      setEmailVerified(currentUser.emailVerified || currentUser.providerData[0]?.providerId === 'google.com');
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    await syncUserProfile(result.user, 'google');
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    isAdmin,
    emailVerified,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    resendVerificationEmail,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


